import React, { useEffect, useState, useMemo, memo, useCallback } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { PencilOutlined, UploadOutlined } from "@toddle-edu/ds-icons";
import {
  Button,
  ButtonDisclosure,
  Dialog,
  Dropdown,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { useTranslation } from "react-i18next";
import { useDispatch } from "Hooks";
import { useUnsavedChanges } from "TranscriptsV3/hooks";
import { useAcademicRecordsModal } from "../../context";
import DisplaySettingsButton from "../DisplaySettingsButton";
import {
  toggleEditMode,
  resetAcademicRecordsModule,
} from "../../module/AcademicRecordsActions";
import { useTranscriptsPermissions } from "TranscriptsV3/context/TranscriptsPermissionContext";
import { canManageAcademicRecords } from "TranscriptsV3/modules/PermissionHelpers";
import {
  updateStudentPersonalDetailsMutationHandler,
  updateStudentTranscriptCommentMutationHandler,
  updateStudentGradeCourseMappingMutationHandler,
  upsertStudentRatingsMutationHandler,
} from "../../module/AcademicRecordsThunks";
import {
  getAcademicRecordsFromData,
  getCommentFieldIdFromData,
  hasUnsavedChangesInAcademicRecords,
} from "../../module/utils";
import classes from "./HeaderRightComponent.scss";
import { TEST_ID_PREFIX, getImportDropdownOptions } from "./modules/constants";
import { getImportOptionClickParams } from "./modules/utils";
import { useReadQuery } from "@apollo/client";
import { compose } from "lib/apolloClient";

const HeaderRightComponent = props => {
  const {
    isEditMode,
    studentId,
    commentFieldQueryRef,
    academicRecordsQueryRef,
    hasUnsavedChanges,
    router,
    route,
    sidebarStudentIds,
    currentStudent,
  } = props;

  const dispatch = useDispatch();
  const { openModal } = useAcademicRecordsModal();

  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const { data: commentFieldData } = useReadQuery(commentFieldQueryRef);
  const { data: academicRecordsData } = useReadQuery(academicRecordsQueryRef);

  const commentFieldId = getCommentFieldIdFromData(commentFieldData);
  const academicRecords = getAcademicRecordsFromData(academicRecordsData);

  const { isBlocked, proceed, reset } = useUnsavedChanges(
    hasUnsavedChanges,
    router,
    route
  );

  const permissionMap = useTranscriptsPermissions();

  const schoolId = _.get(
    currentStudent,
    "yearGroup.grade.curriculumProgram.id"
  );

  const canEdit = canManageAcademicRecords({
    permissionMap,
    schoolId,
  });

  useEffect(() => {
    if (isBlocked) {
      setShowDiscardChangesModal(true);
      reset();
    }
  }, [isBlocked, reset]);

  const { t } = useTranslation();

  const handleToggleEditMode = useCallback(() => {
    dispatch(toggleEditMode());
  }, [dispatch]);

  const studentName = _.get(currentStudent, "name", "");

  const handleImportOptionClick = useCallback(
    ({ key }) => {
      const { modalType, params } = getImportOptionClickParams({
        key,
        sidebarStudentIds,
        studentId,
        studentName,
      });
      openModal(modalType, params);
    },
    [openModal, sidebarStudentIds, studentId, studentName]
  );

  const importDropdownOptions = useMemo(() => getImportDropdownOptions(t), [t]);

  const handleOpenDisplaySettings = useCallback(() => {
    openModal("DISPLAY_SETTINGS");
  }, [openModal]);

  const handleCancelEdit = useCallback(() => {
    if (hasUnsavedChanges) setShowDiscardChangesModal(true);
    else dispatch(resetAcademicRecordsModule());
  }, [hasUnsavedChanges, dispatch]);

  const handleSaveChanges = useCallback(async () => {
    if (!hasUnsavedChanges) {
      dispatch(resetAcademicRecordsModule());
      return true;
    }
    try {
      setShowFullScreenLoader(true);

      await Promise.all([
        dispatch(updateStudentPersonalDetailsMutationHandler({ studentId })),
        dispatch(
          updateStudentTranscriptCommentMutationHandler({ commentFieldId })
        ),
        dispatch(
          updateStudentGradeCourseMappingMutationHandler({ academicRecords })
        ),
        dispatch(upsertStudentRatingsMutationHandler()),
      ]);

      dispatch(resetAcademicRecordsModule());
      return true;
    } catch {
      return false;
    } finally {
      setShowFullScreenLoader(false);
    }
  }, [hasUnsavedChanges, dispatch, studentId, commentFieldId, academicRecords]);

  const handleCloseDiscardChangesModal = useCallback(() => {
    setShowDiscardChangesModal(false);
  }, []);

  const handleModalDiscardChanges = useCallback(() => {
    setShowDiscardChangesModal(false);
    dispatch(resetAcademicRecordsModule());
    proceed();
  }, [dispatch, proceed]);

  const handleModalSaveChanges = useCallback(async () => {
    setShowDiscardChangesModal(false);
    const didSave = await handleSaveChanges();
    if (didSave) proceed();
  }, [handleSaveChanges, proceed]);

  if (!canEdit) return null;

  return (
    <div>
      <div className={classes.buttonContainer}>
        <DisplaySettingsButton
          tooltip={t("common:display_settings")}
          onClick={handleOpenDisplaySettings}
          testId={`${TEST_ID_PREFIX}-display-settings-button`}
        />
        {isEditMode ? (
          <div className={classes.actionGroup}>
            <Button
              variant="neutral"
              type="outlined"
              onClick={handleCancelEdit}
              testId={`${TEST_ID_PREFIX}-cancel-edit-button`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="progressive"
              type="fill"
              onClick={handleSaveChanges}
              testId={`${TEST_ID_PREFIX}-save-button`}
            >
              {t("common:save")}
            </Button>
          </div>
        ) : (
          <>
            <Dropdown
              overlay={
                <DropdownMenu
                  options={importDropdownOptions}
                  onClick={handleImportOptionClick}
                  testId={`${TEST_ID_PREFIX}-import-dropdown-menu`}
                />
              }
            >
              <ButtonDisclosure
                variant="neutral"
                type="outlined"
                icon={<UploadOutlined />}
                testId={`${TEST_ID_PREFIX}-import-dropdown`}
              >
                {t("transcript:import_data_for")}
              </ButtonDisclosure>
            </Dropdown>
            <Button
              variant="destructive"
              type="fill"
              icon={<PencilOutlined />}
              onClick={handleToggleEditMode}
              testId={`${TEST_ID_PREFIX}-edit-data-button`}
            >
              {t("transcript:edit_data")}
            </Button>
          </>
        )}
      </div>

      {showDiscardChangesModal && (
        <Dialog
          variant="confirm-dialog"
          title={t("common:discard_changes")}
          message={t("transcript:unsaved_changes_warning")}
          isOpen={showDiscardChangesModal}
          onClose={handleCloseDiscardChangesModal}
          buttonOneText={t("common:discard")}
          buttonTwoText={t("common:save")}
          onButtonOneClick={handleModalDiscardChanges}
          onButtonTwoClick={handleModalSaveChanges}
          testId={`${TEST_ID_PREFIX}-discard-changes-modal`}
        />
      )}

      {showFullScreenLoader && <FullScreenLoader />}
    </div>
  );
};

const mapStateToProps = state => {
  const { isEditMode } = _.get(state, "transcriptV3.transcriptEditGrades", {});
  const hasUnsavedChanges = hasUnsavedChangesInAcademicRecords({ state });
  return {
    isEditMode,
    hasUnsavedChanges,
  };
};

export default compose(connect(mapStateToProps), memo)(HeaderRightComponent);
